import { useState } from 'react';
import { PDFFont, PDFPage, rgb } from 'pdf-lib';
import { Box, Tabs, Tab, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { IPurchaserInfo } from '../../../../types/CreateDealForm';

type IFintracQuestion = { question: string; extra?: 'Possibly' | 'Unknown'; explain?: boolean };

// FINTRAC risk assessment questions (C. Client Risk, Part 1). Every question has Yes/No; some add a third choice
// (Possibly / Unknown), and the "explain" ones get a text box once they're answered Yes (or Possibly).
// Individuals and corporations answer different questions - each matches its CREA identification record.
export const FINTRAC_QUESTIONS: IFintracQuestion[] = [
  { question: 'Prior to this transaction, did you previously work with this client or have a personal relationship with them?' },
  {
    question:
      'To your knowledge, does the client have a criminal history in regards to illegal drug trafficking, bribery, fraud, forgery, murder, robbery, counterfeit money, stock manipulation, tax evasion or copyright infringement?',
    extra: 'Possibly',
  },
  {
    question:
      'To your knowledge, is there a concern about money laundering or terrorist financing in the geographic location of the property or your brokerage location?',
    extra: 'Possibly',
  },
  { question: 'Does the client live within 10 km of an international border or 5 km of an international airport?' },
  { question: 'Is the client a Canadian citizen or permanent resident?', extra: 'Unknown' },
  { question: 'Is the client a domestic PEP or a family member/close associate of one?' },
  { question: 'Is the client a foreign PEP or HIO or a family member/close associate of one?' },
  { question: 'Is the client subject to a Ministerial Directive? (see Ministerial Directive applicable to North Korea, Russia, etc.).' },
  { question: 'Are you aware of any other connection to the client and a high-risk country? If yes or possibly, explain:', extra: 'Possibly', explain: true },
  {
    question:
      'Has the client attempted to conceal their identity in any way (e.g. use an intermediary like a lawyer, refuse to provide ID at any point, etc.)',
  },
  { question: 'Has your brokerage previously filed a suspicious transaction or terrorist property report on this client?' },
  { question: "Does the transaction seem unusual in light of the client's occupation? If yes, explain:", explain: true },
  { question: 'Does the transaction involve a third party?', extra: 'Possibly' },
  { question: 'Did you ID the client in a non-face-to-face setting?' },
  { question: 'Did the client provide some or all of the deposit in cash?' },
  { question: "Does the transaction seem unusual in light of the client's past transactions with the brokerage?" },
  { question: 'Is there anything else unusual about the client or the transaction? If yes, explain:', explain: true },
];

export const CORP_FINTRAC_QUESTIONS: IFintracQuestion[] = [
  { question: 'Prior to this transaction, did you previously work with this client or have a relationship with them?' },
  {
    question:
      'To your knowledge, does the client have a criminal history in regards to illegal drug trafficking, bribery, fraud, forgery, murder, robbery, counterfeit money, stock manipulation, tax evasion and copyright infringement.',
    extra: 'Possibly',
  },
  {
    question:
      'Is there a concern about money laundering or terrorist financing in the geographic location of the property, your brokerage location or where the client is operating from?',
    extra: 'Possibly',
  },
  { question: 'Is the client situated within 10 km of an international border or 5 km of an international airport?' },
  { question: 'Is the client a Canadian corporation or entity?', extra: 'Unknown' },
  { question: 'Is the client subject to a Ministerial Directive? (see Ministerial Directive applicable to North Korea, Russia, etc.).' },
  { question: 'Are you aware of any other connection to the client and a high-risk country? If yes or possibly, explain:', extra: 'Possibly', explain: true },
  { question: 'Has the client attempted to conceal its identity in any way (e.g. use an intermediary like a lawyer, refuse to provide ID at any point, etc.)' },
  { question: 'Has your brokerage previously filed a suspicious transaction or terrorist property report on this client?' },
  { question: "Does the transaction seem unusual in light of the client's business? If yes, explain:", explain: true },
  { question: 'Does the transaction involve a third party?', extra: 'Possibly' },
  { question: 'Did you verify the existence of the client in a non-face-to-face setting?' },
  { question: 'Did the client provide some or all of the deposit in cash?' },
  { question: "Does the transaction seem unusual in light of the client's past transactions with the brokerage?" },
  { question: 'Does the client appear to be a shell company (a corporation without active business operations or significant assets?)', extra: 'Possibly' },
  { question: 'Does the client operate a cash-intensive business (e.g. a bar, club, laundromat, etc.) or offer online gambling?', extra: 'Possibly' },
  { question: 'Is there anything else unusual about the client or the transaction? If yes, explain:', explain: true },
];

export const fintracQuestionsFor = (purchaser: IPurchaserInfo) => (purchaser.corp ? CORP_FINTRAC_QUESTIONS : FINTRAC_QUESTIONS);

// Where everything sits on each CREA identification record. Both are scans (300dpi), so every position was measured
// from the page images: centres in PDF points from the page's top-left. Page indexes are 0-based.
interface IFintracFormLayout {
  // Page 2 B.1 "Is the transaction being conducted on behalf of a third party?" - the No box
  thirdPartyNo: { page: number; x: number; y: number };
  // Page 3 Part 1 answer boxes: one row per question; the third column (Possibly/Unknown) can sit a little lower
  risk: { page: number; columnX: [number, number, number]; rowY: number[]; extraRowY: Record<number, number> };
  // Page 3 Part 2 points per question for Yes / No / Unknown-Possibly (null = N/A, counts as 0); Q1-9 in the
  // left table, Q10-17 in the right, whose 9th row is Total
  score: { points: [number, number, number | null][]; leftX: number[]; rightX: number[]; rowY: number[] };
}

const INDIVIDUAL_LAYOUT: IFintracFormLayout = {
  thirdPartyNo: { page: 1, x: 57.8, y: 343.4 },
  risk: {
    page: 2,
    columnX: [437.9, 478.9, 523.6],
    rowY: [116.4, 138.8, 165.8, 188.4, 206.4, 224.4, 242.4, 260.4, 278.4, 300.8, 323.4, 345.8, 368.4, 386.4, 404.4, 422.4, 444.8],
    extraRowY: {},
  },
  score: {
    points: [
      [0, 2, 0],
      [10, 0, 1],
      [2, 0, 1],
      [2, 0, null],
      [0, 2, 1],
      [6, 0, null],
      [10, 0, null],
      [10, 0, null],
      [5, 0, 1],
      [6, 0, null],
      [10, 0, null],
      [5, 0, null],
      [2, 0, 1],
      [2, 0, null],
      [3, 0, null],
      [4, 0, null],
      [4, 0, null],
    ],
    leftX: [147.1, 196.8, 259.4],
    rightX: [353.5, 405.4, 471.6],
    rowY: [508.8, 526.1, 543.4, 560.6, 577.9, 595.2, 612.2, 629.3, 646.6],
  },
};

const CORP_LAYOUT: IFintracFormLayout = {
  thirdPartyNo: { page: 1, x: 59.0, y: 362.8 },
  risk: {
    page: 2,
    columnX: [439.3, 478.2, 522.8],
    rowY: [116.8, 137.4, 161.4, 182.5, 200.8, 218.5, 236.8, 257.4, 278.5, 299.6, 320.8, 338.5, 356.8, 374.5, 392.8, 410.5, 431.6],
    // Question index -> y of its Possibly/Unknown box
    extraRowY: { 1: 138.8, 2: 162.8, 4: 201.7, 6: 238.2, 10: 322.2, 14: 394.2, 15: 412.0 },
  },
  score: {
    points: [
      [0, 2, 0],
      [10, 0, 1],
      [2, 0, 1],
      [2, 0, null],
      [0, 2, 1],
      [10, 0, null],
      [5, 0, 1],
      [6, 0, null],
      [10, 0, null],
      [5, 0, null],
      [2, 0, 1],
      [2, 0, null],
      [3, 0, null],
      [4, 0, null],
      [5, 0, 1],
      [5, 0, 1],
      [4, 0, null],
    ],
    leftX: [143.5, 195.4, 260.9],
    rightX: [357.4, 409.2, 475.4],
    rowY: [499.7, 517.0, 534.0, 551.3, 568.8, 586.1, 603.4, 620.6, 637.9],
  },
};

export type IFintracForm = 'individual' | 'corp';
const LAYOUTS: Record<IFintracForm, IFintracFormLayout> = { individual: INDIVIDUAL_LAYOUT, corp: CORP_LAYOUT };

// Page 1 (A.1, "1. Type of Identification Document") of the individual record: the three checkbox centres and where
// the "Other, explain" dotted line starts
const ID_TYPE_PAGE_INDEX = 0;
const ID_TYPE_ROW_Y = 350.6;
const ID_TYPE_BOX_X = { driver: 190.4, passport: 257.0, other: 307.7 };
const ID_TYPE_OTHER_TEXT = { x: 366, y: 353.5 };

// Page 4 D.1 "Acting as an agent for the purchase or sale of". The individual record has Residential and Commercial
// property boxes; the corporate one has Commercial property and Other (residential goes under Other, written in)
const INDIVIDUAL_PROPERTY = { page: 3, x: 57.4, residential: 147.1, commercial: 162.5 };
const CORP_PROPERTY = { page: 3, x: 56.5, commercial: 172.7, other: 189.0, otherText: { x: 158, y: 191.5 } };

// Half the width of an X: the small boxes are ~6.2pt square, the page 3 Part 1 boxes ~7.9pt
const SMALL_X_HALF = 2.2;
const RISK_X_HALF = 3;
const RED = rgb(0.85, 0, 0);

const drawX = (page: PDFPage, x: number, yFromTop: number, half: number) => {
  const y = page.getHeight() - yFromTop;
  const options = { thickness: 1.2, color: rgb(0, 0, 0) };
  page.drawLine({ start: { x: x - half, y: y - half }, end: { x: x + half, y: y + half }, ...options });
  page.drawLine({ start: { x: x - half, y: y + half }, end: { x: x + half, y: y - half }, ...options });
};

// Yes / No / Unknown-Possibly -> column 0 / 1 / 2
const answerColumn = (answer: string): number =>
  answer === 'Yes' ? 0 : answer === 'No' ? 1 : answer === 'Possibly' || answer === 'Unknown' ? 2 : -1;

// X's the purchaser's ID type on page 1 of the individual record: Driver license or Passport, otherwise Other with
// the type written beside it
export const drawFintracIdType = (pages: PDFPage[], idType: string | undefined, font: PDFFont) => {
  const page = pages[ID_TYPE_PAGE_INDEX];
  if (!page || !idType || !idType.trim()) return;
  if (/driver/i.test(idType)) {
    drawX(page, ID_TYPE_BOX_X.driver, ID_TYPE_ROW_Y, SMALL_X_HALF);
  } else if (/passport/i.test(idType)) {
    drawX(page, ID_TYPE_BOX_X.passport, ID_TYPE_ROW_Y, SMALL_X_HALF);
  } else {
    drawX(page, ID_TYPE_BOX_X.other, ID_TYPE_ROW_Y, SMALL_X_HALF);
    page.drawText(idType, { x: ID_TYPE_OTHER_TEXT.x, y: page.getHeight() - ID_TYPE_OTHER_TEXT.y, size: 9, font, color: rgb(0, 0, 0) });
  }
};

// Page 2: always No on the third party question. Page 4: the property type from the unit type.
export const drawFintracFixedAnswers = (pages: PDFPage[], unitType: string | undefined, form: IFintracForm, font: PDFFont) => {
  const { thirdPartyNo } = LAYOUTS[form];
  const thirdPartyPage = pages[thirdPartyNo.page];
  if (thirdPartyPage) drawX(thirdPartyPage, thirdPartyNo.x, thirdPartyNo.y, SMALL_X_HALF);

  const type = (unitType || '').toLowerCase();
  if (form === 'individual') {
    const page = pages[INDIVIDUAL_PROPERTY.page];
    if (page && (type === 'residential' || type === 'commercial')) {
      drawX(page, INDIVIDUAL_PROPERTY.x, INDIVIDUAL_PROPERTY[type], SMALL_X_HALF);
    }
  } else {
    const page = pages[CORP_PROPERTY.page];
    if (!page) return;
    if (type === 'commercial') {
      drawX(page, CORP_PROPERTY.x, CORP_PROPERTY.commercial, SMALL_X_HALF);
    } else if (type === 'residential') {
      drawX(page, CORP_PROPERTY.x, CORP_PROPERTY.other, SMALL_X_HALF);
      page.drawText('Residential', {
        x: CORP_PROPERTY.otherText.x,
        y: page.getHeight() - CORP_PROPERTY.otherText.y,
        size: 9,
        font,
        color: rgb(0, 0, 0),
      });
    }
  }
};

// Page 3 Part 1: an X in the box for each answered question
export const drawFintracRiskAnswers = (pages: PDFPage[], answers: Record<number, IFintracAnswer> | undefined, form: IFintracForm) => {
  const { risk } = LAYOUTS[form];
  const page = pages[risk.page];
  if (!page || !answers) return;
  Object.entries(answers).forEach(([key, { answer }]) => {
    const index = Number(key);
    const column = answerColumn(answer);
    const rowY = risk.rowY[index];
    if (column < 0 || rowY === undefined) return;
    const extraY = risk.extraRowY[index];
    const y = column === 2 && extraY !== undefined ? extraY : rowY;
    drawX(page, risk.columnX[column], y, RISK_X_HALF);
  });
};

// Page 3 Part 2: circles each answered question's points in red and writes the total in the Total row
export const drawFintracRiskScore = (
  pages: PDFPage[],
  answers: Record<number, IFintracAnswer> | undefined,
  font: PDFFont,
  form: IFintracForm
) => {
  const { risk, score } = LAYOUTS[form];
  const page = pages[risk.page];
  if (!page || !answers) return;
  let total = 0;
  let answered = 0;

  Object.entries(answers).forEach(([key, { answer }]) => {
    const index = Number(key);
    const column = answerColumn(answer);
    const points = score.points[index];
    if (column < 0 || !points) return;
    answered += 1;
    total += points[column] ?? 0;

    const right = index >= 9;
    const x = (right ? score.rightX : score.leftX)[column];
    const yFromTop = score.rowY[right ? index - 9 : index];
    // N/A is wider than the numbers, so give it a wider circle
    page.drawEllipse({
      x,
      y: page.getHeight() - yFromTop,
      xScale: points[column] === null ? 13 : 10,
      yScale: 7,
      borderColor: RED,
      borderWidth: 1.2,
    });
  });

  if (!answered) return;
  const text = `${total}`;
  const size = 11;
  page.drawText(text, {
    x: score.rightX[0] - font.widthOfTextAtSize(text, size) / 2,
    y: page.getHeight() - score.rowY[8] - size / 3,
    size,
    font,
    color: RED,
  });
};

export interface IFintracAnswer {
  answer: string;
  explanation: string;
}

// Answers keyed by purchaser _id, then by question index
export type IFintracAnswers = Record<string, Record<number, IFintracAnswer>>;

const FintracQuestions = ({ purchasers, answers, setAnswers }: ChildProps) => {
  const [tab, setTab] = useState<number>(0);
  const purchaser = purchasers[tab];

  if (!purchaser) return null;

  const purchaserId = purchaser._id!;
  const purchaserAnswers = answers[purchaserId] || {};
  const questions = fintracQuestionsFor(purchaser);

  const updateAnswer = (index: number, change: Partial<IFintracAnswer>) => {
    const current = purchaserAnswers[index] || { answer: '', explanation: '' };
    setAnswers({ ...answers, [purchaserId]: { ...purchaserAnswers, [index]: { ...current, ...change } } });
  };

  // Behaves like a radio group but looks like the form's checkboxes; clicking the checked one clears it
  const toggleAnswer = (index: number, choice: string) => {
    const current = purchaserAnswers[index]?.answer;
    updateAnswer(index, { answer: current === choice ? '' : choice });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {purchasers.map((p: IPurchaserInfo, index: number) => (
          <Tab key={p._id || index} label={`${p.firstName} ${p.lastName}`} sx={{ textTransform: 'none' }} />
        ))}
      </Tabs>
      {purchaser.corp ? (
        <Typography sx={{ fontSize: '0.8rem', color: '#6a6a67', mt: 1 }}>Corporation / entity questions</Typography>
      ) : null}
      <Box>
        {questions.map((item, index) => {
          const current = purchaserAnswers[index] || { answer: '', explanation: '' };
          const choices = ['Yes', 'No', ...(item.extra ? [item.extra] : [])];
          const showExplain = item.explain && (current.answer === 'Yes' || current.answer === 'Possibly');
          return (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1, borderBottom: '1px solid #e4e4e0', flexWrap: { xs: 'wrap', md: 'nowrap' } }}
            >
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, minWidth: '22px' }}>{index + 1}</Typography>
              <Box sx={{ flex: 1, minWidth: '240px' }}>
                <Typography sx={{ fontSize: '0.85rem' }}>{item.question}</Typography>
                {showExplain ? (
                  <TextField
                    sx={{ mt: 1 }}
                    size="small"
                    fullWidth
                    multiline
                    placeholder="Explain"
                    value={current.explanation}
                    onChange={(e) => updateAnswer(index, { explanation: e.target.value })}
                  />
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', flexShrink: 0, width: { md: '300px' } }}>
                {choices.map((choice) => (
                  <FormControlLabel
                    key={choice}
                    sx={{ width: '100px', mr: 0 }}
                    control={<Checkbox size="small" checked={current.answer === choice} onChange={() => toggleAnswer(index, choice)} />}
                    label={<Typography sx={{ fontSize: '0.85rem' }}>{choice}</Typography>}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

interface ChildProps {
  purchasers: IPurchaserInfo[];
  answers: IFintracAnswers;
  setAnswers: (answers: IFintracAnswers) => void;
}

export default FintracQuestions;
